/* eslint-disable @next/next/no-img-element */
import { GameType } from '@daymade/common-lib/dbEnums';
import NewEntryLine from 'components/enter/NewEntryLine';
import WeeklyDrawNumberPickerPopup from 'components/WeeklyDrawNumberPickerPopup';
import { useDrawStore } from 'hooks/stores/useDrawStore';
import { observer } from 'mobx-react-lite';
import React, { useState } from 'react';
import { Message } from 'semantic-ui-react';
import { IEntryLine } from 'types/Draws';
import { extendClassName } from 'utils/common';
import { ASSET_PREFIX, colors } from 'utils/constants';
import {
  entryLineContainsDuplicates,
  lineHasDuplicates,
  shouldShowErrorForNumberChoice,
} from 'utils/LottoEntryValidator';

interface INewSelectEntriesSection {
  hideRemoveEntryButtons?: boolean;
  lines: IEntryLine[];
  setLines: (lines: IEntryLine[]) => void;
  setLineNumber: (line_number: number, position: number, value: number) => void;
  setLineBonusNumber: (
    line_number: number,
    position: number,
    value: number
  ) => void;
  removeLine: (line_number: number) => void;
  generateLineRandomNumbers: (lineNumber: number) => void;
  locked: boolean;
  lockEntriesHandler?: () => void;
  lockEntriesTitle?: string;
  loading: boolean;
  totalComponent?: React.ReactNode;
  optionalHeadingComponent?: React.ReactNode;
  glowColor: string;
  noBorder: boolean;
  additionalClassName?: string;
}

const NewSelectEntriesSection = observer(
  ({
    hideRemoveEntryButtons,
    lines,
    setLines,
    setLineNumber,
    setLineBonusNumber,
    generateLineRandomNumbers,
    removeLine,
    locked,
    optionalHeadingComponent,
    glowColor = '#68FADE',
    noBorder = false,
    additionalClassName,
  }: INewSelectEntriesSection) => {
    const { numberSetup } = useDrawStore();
    if (!numberSetup) throw new Error('Missing required prop: ballRanges');

    const [showNumberPickerPopup, setShowNumberPickerPopup] = useState(false);
    const [editingLine, setEditingLine] = useState(0);
    const [editingNumberIdx, setEditingNumberIdx] = useState<number | null>(
      null
    );

    const renderEntryErrorMessage = (line: IEntryLine) => {
      if (line.main.includes(null)) return null;

      if (entryLineContainsDuplicates(line)) {
        return (
          <Message
            negative
            style={{ margin: '1em' }}
          >
            <Message.Header>Entry Contains Duplicates</Message.Header>

            <p>
              Your{' '}
              {numberSetup.bonus && numberSetup.bonus.length > 0
                ? 'main numbers'
                : 'entry'}{' '}
              cannot contain any duplicates.
            </p>
          </Message>
        );
      }

      if (lineHasDuplicates(line, lines)) {
        return (
          <Message
            negative
            style={{ margin: '1em' }}
          >
            <Message.Header>Duplicate Entries</Message.Header>

            <p>You cannot have duplicate entries in a draw.</p>
          </Message>
        );
      }

      for (let i = 0; i < line.main.length; i++) {
        if (
          line.main[i] &&
          shouldShowErrorForNumberChoice(line.main[i]!, numberSetup.main[i])
        )
          return (
            <Message
              negative
              style={{ margin: '1em' }}
            >
              <Message.Header>Invalid Entry</Message.Header>

              <p>
                "{line.main[i]}" is not within the allowed range (1 -{' '}
                {numberSetup.main[i]}
                ).
              </p>
            </Message>
          );
      }
    };

    const updateLine = (mainNumbers: number[], bonusNumbers: number[]) => {
      const newLines = [...lines];
      newLines[editingLine] = {
        type: GameType.LOTTERY,
        main: mainNumbers,
        bonus: bonusNumbers,
      };
      setLines(newLines);
    };

    return (
      <div
        className={extendClassName(
          'selectEntriesSection flexCentered',
          additionalClassName
        )}
      >
        {editingLine != null ? (
          <WeeklyDrawNumberPickerPopup
            numberRanges={numberSetup.main}
            bonusNumberRanges={numberSetup.bonus}
            showPopup={showNumberPickerPopup}
            prepopulateMainNumbers={lines[editingLine]?.main.filter(
              (value) => value != null
            )}
            prepopulateBonusNumbers={lines[editingLine]?.bonus.filter(
              (value) => value != null
            )}
            onConfirmNumbers={updateLine}
            onClose={() => setShowNumberPickerPopup(false)}
            glowColor={glowColor}
          />
        ) : null}

        <div className="fullWidth grid gap-2em">
          {optionalHeadingComponent}

          <div className="grid gap-1em">
            {lines.map((line: IEntryLine, index: number) => {
              let trashButtonContainerName = 'trashButtonContainer';
              if (locked || hideRemoveEntryButtons || lines.length <= 1) {
                trashButtonContainerName += ' invisible';
              }

              return (
                <div
                  key={index}
                  className="lineSelectorContainer fullWidth flexCentered"
                >
                  <div className="lineContainer fullWidth flexRow">
                    <div
                      className="shuffleButton flexCentered"
                      onClick={() => generateLineRandomNumbers(index)}
                    >
                      <img
                        className="randomIcon"
                        src={`${ASSET_PREFIX}/images/enter/shuffle-entry.svg`}
                      />
                    </div>

                    <NewEntryLine
                      line={line}
                      lineIndex={index}
                      key={index}
                      setLineNumber={setLineNumber}
                      setLineBonusNumber={setLineBonusNumber}
                      numberRanges={numberSetup.main}
                      bonusNumberRanges={numberSetup.bonus}
                      useNumberPicker
                      disabled={locked}
                      onClick={(ballIdx: number) => {
                        setEditingNumberIdx(ballIdx);
                        setEditingLine(index);
                        setShowNumberPickerPopup(true);
                      }}
                      className={
                        lines[index].main.includes(null) ? 'animated' : ''
                      }
                      glowColor={glowColor}
                      style={undefined}
                    />

                    <div
                      className={trashButtonContainerName}
                      onClick={() => {
                        if (lines.length <= 1) return;
                        removeLine(index);
                      }}
                    >
                      <img
                        src={`${ASSET_PREFIX}/images/close_button.svg`}
                        style={{ width: '13px' }}
                      />
                    </div>
                  </div>

                  {renderEntryErrorMessage(line)}
                </div>
              );
            })}
          </div>
        </div>

        <style jsx>{`
          .selectEntriesSection {
            width: 100%;
            border: ${noBorder ? 'unset' : '3px solid #E5E5E5'};
            border-radius: 10px;
          }

          .lineContainer {
            display: flex;
            align-items: center;
            max-width: 450px;
          }

          :global(.lineContainer .animated) {
            -webkit-animation: shake 4s ease;
            animation: shake 4s ease;
            -webkit-animation-iteration-count: infinite;
            animation-iteration-count: infinite;
          }

          :global(.lineContainer .animated:hover) {
            -webkit-animation: none;
            animation: none;
          }

          .lineContainer :global(.trashButtonContainer) {
            display: flex;
            justify-content: center;
            align-items: center;
            align-self: center;
            padding: 6px 0 6px 6px;
            cursor: pointer;
          }

          .lineContainer .trashButtonContainer:hover :global(.trashIcon) {
            color: #555;
          }

          .lineContainer :global(.trashIcon) {
            color: #808080;
            width: 12px;
          }

          @media (min-width: 400px) {
            .lineContainer :global(.trashIcon) {
              width: 15px;
            }
          }

          .lineContainer :global(.trashIcon.disabled) {
            color: #ddd;
            visibility: hidden;
          }

          .lineContainer :global(.trashButtonContainer.disabled) {
            cursor: default;
            pointer-events: none;
            -webkit-filter: grayscale(100%); /* Safari 6.0 - 9.0 */
            filter: grayscale(100%);
          }

          .lineContainer :global(.trashButtonContainer.invisible) {
            display: none;
          }

          .randomIcon {
            position: absolute;
            top: 27%;
            height: 46%;
          }

          :global(.lineContainer .shuffleButton) {
            border-radius: 100%;
            background: ${colors.primaryCta};
            cursor: pointer;
            margin-right: 0.2em;
            width: 13%;
            padding-top: 13%;
            position: relative;
          }

          :global(.lineContainer .shuffleButton:hover) {
            background: ${colors.primaryCtaPressed};
          }

          :global(.lineContainer .shuffleButton:hover > img) {
            -webkit-animation: grow 0.2s;
            animation: grow 0.2s;
            -webkit-animation-iteration-count: 1;
            animation-iteration-count: 1;
            transform: scale(1.1);
          }

          .addAnotherEntryButton {
            margin: 5px auto 10px auto;
            white-space: nowrap;
            text-align: center;
            color: ${colors.primaryBrandGray};
          }

          @media (min-width: 410px) {
            :global(.lineContainer .shuffleButton) {
              margin-right: 0.5em;
            }
          }

          @media (min-width: 475px) {
            :global(.lineContainer .shuffleButton) {
              margin-right: 0.5em;
            }

            .addAnotherEntryButton {
              font-size: 1.1rem;
            }
          }

          @keyframes grow {
            from {
              transform: scale(1);
            }
            to {
              transform: scale(1.1);
            }
          }

          @-webkit-keyframes shake {
            3% {
              -webkit-transform: translateX(4px);
              transform: translateX(4px);
            }
            6% {
              -webkit-transform: translateX(-4px);
              transform: translateX(-4px);
            }
            9% {
              -webkit-transform: translateX(3px);
              transform: translateX(3px);
            }
            12% {
              -webkit-transform: translateX(-3px);
              transform: translateX(-3px);
            }
            15% {
              -webkit-transform: translateX(2px);
              transform: translateX(2px);
            }
            18% {
              -webkit-transform: translateX(-2px);
              transform: translateX(-2px);
            }
            21% {
              -webkit-transform: translateX(1px);
              transform: translateX(1px);
            }
            24% {
              -webkit-transform: translateX(-1px);
              transform: translateX(-1px);
            }
            27% {
              -webkit-transform: translateX(0px);
              transform: translateX(0px);
            }
          }

          @keyframes shake {
            3% {
              -webkit-transform: translateX(4px);
              transform: translateX(4px);
            }
            6% {
              -webkit-transform: translateX(-4px);
              transform: translateX(-4px);
            }
            9% {
              -webkit-transform: translateX(3px);
              transform: translateX(3px);
            }
            12% {
              -webkit-transform: translateX(-3px);
              transform: translateX(-3px);
            }
            15% {
              -webkit-transform: translateX(2px);
              transform: translateX(2px);
            }
            18% {
              -webkit-transform: translateX(-2px);
              transform: translateX(-2px);
            }
            21% {
              -webkit-transform: translateX(1px);
              transform: translateX(1px);
            }
            24% {
              -webkit-transform: translateX(-1px);
              transform: translateX(-1px);
            }
            27% {
              -webkit-transform: translateX(0px);
              transform: translateX(0px);
            }
          }
        `}</style>
      </div>
    );
  }
);

export default NewSelectEntriesSection;
